import { useRouter } from 'next/router';
import React, { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import CoursesCard from './CoursesCard';
import CoursesSkeleton from './CoursesSkeleton';
import { ShouldRender } from '../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../shared/formateErrorObject';
import Heading from '../../shared/Heading';
import CustomLoader from '../../shared/Loading';
import { notify } from '../../shared/notify';
import { getAllProducts } from '../../../services/Products';
import { PaginationProps } from '../../../utils/interfaces/Orders';
import { BookItems } from '../../../utils/interfaces/Products/books';

interface Props {
  heading?: string;
  isPagination?: boolean;
}

const CoursesSection: React.FC<Props> = ({ isPagination = false }) => {
  const [loading, setLoading] = useState(false);
  const [courseData, setCoursesData] = useState<BookItems[]>([]);
  const [paginationData, setPaginationData] = useState<PaginationProps>();
  const [page, setPage] = useState<number>(1);
  const router = useRouter();

  const { ref: inViewRef, inView } = useInView({
    threshold: 1,
    triggerOnce: false,
  });

  const handleCourseDetails = async () => {
    if (loading) return;

    try {
      setLoading(true);
      const data = await getAllProducts(page, 5, undefined, undefined, 3);
      const formattedData = formatCoursesData(data?.data);

      setPaginationData({
        current_page: data?.meta?.current_page,
        per_page: data?.meta?.per_page,
        total: data?.meta?.total,
      });

      setCoursesData((prevCourseData) => {
        const uniqueData = prevCourseData.filter(
          (prevItem) =>
            !formattedData.some(
              (newItem) => newItem.hardCopyId === prevItem.hardCopyId,
            ),
        );
        return [...uniqueData, ...formattedData];
      });
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatCoursesData = (rawData: any[]): BookItems[] => {
    return rawData.map((courses) => ({
      hardCopyId: courses?.id || '',
      bookImage: courses?.thumbnail_image || '',
      heading: courses?.name || '',
      authorName: courses?.author?.name || '',
      desc: courses?.description || '',
      slug: courses?.slug || '',
      hardCopyPrice: courses?.main_price || 0,
      softCopyPrice: courses?.main_price || 0,
      authorImage: courses?.author?.image_url || '',
      type: courses?.category || '',
      actualPriceHardCopy: courses?.stroked_price || 0,
      actualPriceSoftCopy: courses?.digital_pair?.stroked_price || 0,
      external_link: courses?.external_link || '',
      is_available: courses?.is_available && courses?.i18n_attributes,
      currency: courses?.currency_symbol,
      i18n_attributes: courses?.i18n_attributes || {},
    }));
  };

  useEffect(() => {
    handleCourseDetails();
  }, [page]);

  useEffect(() => {
    if (
      inView &&
      !loading &&
      paginationData?.current_page !== paginationData?.total
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  }, [inView]);

  const totalPages = useMemo(
    () =>
      Math.ceil((paginationData?.total || 0) / (paginationData?.per_page || 6)),
    [paginationData?.total, paginationData?.per_page],
  );

  const isLastPage = useMemo(() => page >= totalPages, [page, totalPages]);
  return (
    <>
      <ShouldRender check={courseData.length}>
        <div>
          <Heading heading='Courses' route='/product/courses-list/' />
          <div className='flex justify-center w-full'>
            <div
              className='grid md:grid-cols-2 w-full xl:grid-cols-3 gap-6 lg:gap-8 xl:gap-10'
              style={{ alignItems: 'stretch' }}
            >
              {courseData?.map((course, index) => (
                <div
                  key={index}
                  onClick={() => {
                    const slug = course?.slug;

                    router.push(`/courses/${slug}`);
                  }}
                  className='flex'
                >
                  <CoursesCard key={index} productData={course as BookItems} />
                </div>
              ))}
              <ShouldRender check={loading}>
                {Array.from({ length: 6 }).map((_, index) => (
                  <CoursesSkeleton key={index} />
                ))}
              </ShouldRender>
            </div>
          </div>
          <ShouldRender check={isPagination && !isLastPage}>
            <div ref={inViewRef} className='flex justify-center py-20'>
              {loading && <CustomLoader />}
            </div>
          </ShouldRender>
        </div>
      </ShouldRender>
    </>
  );
};

export default CoursesSection;
